import {
  AuthProvider,
  hasAuthParams,
  useAuth,
} from "react-oidc-context";
import { useEffect, useState, useRef, lazy, Suspense } from "react";
import {
  WebStorageStateStore,
  UserManager,
  type UserManagerSettings,
} from "oidc-client-ts";
import OidcApolloProvider from "@/providers/OidcApolloProvider";
import { ImpersonationProvider, ImpersonationConsumer } from "@/providers/ImpersonationProvider";
import { Outlet, useNavigate } from "react-router-dom";
import { SentryErrorBoundary } from "@/components/SentryErrorBoundary";
import * as Sentry from "@sentry/react";
import Loading from "@/components/Loading";
import { ProfileProvider } from "@/providers/ProfileProvider";
import { LanguageSync } from "@/components/LanguageSync";
import TenantNotLicensedScreen from "@/components/auth/TenantNotLicensedScreen";

const BrandGradient = lazy(() => import("@/components/BrandGradient"));
import { TenantSelectionProvider } from "@/providers/TenantSelectionProvider";
import { CONFIG } from "@/consts/config";
import { setAccessToken } from "./OidcProtectedLayout.utils";
import { SilentSessionRestore } from "@lytra/frontend-common";
import { PersonAvatarsProvider } from "@/components/PersonAvatar";

const oidcSettings: UserManagerSettings = {
  authority: CONFIG.oidcAuthority,
  client_id: CONFIG.oidcClientId,
  scope: "openid profile email offline_access",
  // Use current window origin (works with any port)
  redirect_uri: window.location.origin + "/auth/callback",
  post_logout_redirect_uri: window.location.origin,
  silent_redirect_uri: window.location.origin + "/auth/silent_callback",
  loadUserInfo: true,
  userStore: new WebStorageStateStore({ store: window.localStorage }),
  automaticSilentRenew: true,
  silentRequestTimeoutInSeconds: 20,
};

// The single UserManager for the app: handed to AuthProvider below and imported
// by Apollo error handling. Letting AuthProvider build its own would give us two
// managers renewing the same stored session.
export const userManager = new UserManager(oidcSettings);

// Silent-renew failure is a UserManager event, not an AuthProvider prop.
// Deliberately no redirect: let the user keep working, the next request that
// needs a token surfaces the problem via useAuth().error.
userManager.events.addSilentRenewError((error) => {
  console.warn("Silent token renewal failed:", error);
});

// Raised when the session ends at the OP. Requires `monitorSession: true` in
// oidcSettings (default false), so today this only fires if that is enabled.
userManager.events.addUserSignedOut(() => {
  console.log("User session expired, redirecting to login");
  window.location.href = "/";
});

function GradientScreen({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative h-screen w-screen flex items-center justify-center overflow-hidden">
      <Suspense fallback={null}>
        <BrandGradient />
      </Suspense>
      <div className="relative z-10">{children}</div>
    </div>
  );
}

function ProviderLayout() {
  const auth = useAuth();
  const { isAuthenticated, user, isLoading, error } = auth;
  // Set by the Apollo error link when Jupiter rejects a request with
  // TENANT_NOT_LICENSED (authenticated via Microsoft broker, but the email
  // domain has no tenant_domain mapping -- see jupiter/dependencies.py).
  const [tenantNotLicensed, setTenantNotLicensed] = useState(false);

  useEffect(() => {
    if (user?.access_token) {
      setAccessToken(user.access_token);
      Sentry.setUser({
        id: user.profile.sub,
        email: user.profile.email ?? undefined,
        username:
          `${user.profile.given_name ?? ""} ${user.profile.family_name ?? ""}`.trim() ||
          undefined,
      });
    } else {
      setAccessToken(undefined);
      Sentry.setUser(null);
    }
  }, [user]);

  // Only show loading on very first load when we don't have user data yet
  // Don't show loading on subsequent navigation/re-renders if we already have a user
  if (isLoading && !user) {
    return (
      <GradientScreen>
        <Loading />
      </GradientScreen>
    );
  }

  // Handle authentication errors gracefully
  if (error) {
    console.error("Authentication error:", error);
    return (
      <GradientScreen>
        <div className="text-center">
          <h1 className="text-xl font-bold mb-2">Session Expired</h1>
          <p className="text-gray-600 mb-4">Please sign in again to continue</p>
          <button
            className="px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90"
            onClick={() => window.location.reload()}
          >
            Sign In
          </button>
        </div>
      </GradientScreen>
    );
  }

  if (!isAuthenticated || !user) return null;

  if (tenantNotLicensed) {
    return (
      <TenantNotLicensedScreen
        onSignOut={() => {
          setTenantNotLicensed(false);
          void auth.removeUser().finally(() => void auth.signoutRedirect());
        }}
      />
    );
  }

  // The realm role the backend checks too; the realm config puts it in the ID token.
  const realmRoles = (user.profile.realm_access as { roles?: string[] } | undefined)?.roles;

  return (
    <ImpersonationProvider isLytraStaff={realmRoles?.includes("lytra_staff") ?? false}>
      <ImpersonationConsumer>
        {({ impersonateTenant }) => (
          <OidcApolloProvider
            token={user.access_token}
            impersonateTenantId={impersonateTenant}
            onTenantNotLicensed={() => setTenantNotLicensed(true)}
          >
            <SentryErrorBoundary>
              <ProfileProvider
                onSessionExpired={() => {
                  rememberReturnTo();
                  void auth.removeUser().finally(() => void auth.signinRedirect());
                }}
              >
                <LanguageSync />
                <TenantSelectionProvider>
                  <PersonAvatarsProvider>
                    <Outlet />
                  </PersonAvatarsProvider>
                </TenantSelectionProvider>
              </ProfileProvider>
            </SentryErrorBoundary>
          </OidcApolloProvider>
        )}
      </ImpersonationConsumer>
    </ImpersonationProvider>
  );
}

function AuthTransitionScreen() {
  return (
    <div className="h-screen w-screen flex items-center justify-center bg-gradient-to-br from-[#D571E5] via-[#80E4FB] to-[#F5F886]">
      <Loading />
    </div>
  );
}

function rememberReturnTo() {
  localStorage.setItem(
    "returnTo",
    window.location.pathname + window.location.search,
  );
}

/**
 * Gate rendered instead of the auto-redirecting `withAuthenticationRequired`
 * HOC: when unauthenticated it redirects straight to Keycloak's hosted login
 * form, which offers both the default login and "Login with Microsoft"
 * natively (see src/keycloakify/src/login/pages/Login.tsx) -- no branded
 * intermediate chooser screen in Venus itself.
 */
function ProtectedLayout() {
  const auth = useAuth();
  const hasRedirectedRef = useRef(false);

  useEffect(() => {
    if (!auth.isLoading && !auth.isAuthenticated && !hasRedirectedRef.current) {
      hasRedirectedRef.current = true;
      rememberReturnTo();
      void auth.signinRedirect();
    }
  }, [auth.isLoading, auth.isAuthenticated, auth]);

  if (!auth.isAuthenticated) {
    return <AuthTransitionScreen />;
  }

  return <ProviderLayout />;
}

function OidcWrapper() {
  const navigate = useNavigate();

  const onSigninCallback = () => {
    window.history.replaceState({}, document.title, window.location.pathname);

    const stored = localStorage.getItem("returnTo") ?? "/";
    localStorage.removeItem("returnTo");
    // Never navigate back to an auth route — it renders Fragment and leaves the
    // user on a blank page with no recovery. Fall back to root instead.
    const returnTo = stored.startsWith("/auth/") ? "/" : stored;
    void navigate(returnTo);
  };

  return (
    <AuthProvider userManager={userManager} onSigninCallback={onSigninCallback}>
      <SilentSessionRestore
        session={userManager}
        skip={hasAuthParams()}
        fallback={<AuthTransitionScreen />}
      >
        <ProtectedLayout />
      </SilentSessionRestore>
    </AuthProvider>
  );
}

export default function OidcProtectedLayout() {
  return <OidcWrapper />;
}
